import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'YOUR_SUPABASE_URL'
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'YOUR_SUPABASE_ANON_KEY'

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// ─── Auth ────────────────────────────────────────────────────────────────────

// Sign in with email + password. Optional captchaToken is forwarded to
// Supabase Auth -- required when CAPTCHA protection is enabled on the
// project (Settings -> Authentication -> Bot and Abuse Protection).
// Pages that call this without a token will get "captcha protection:
// request disallowed" back from Supabase.
export async function signIn(email, password, captchaToken) {
  return supabase.auth.signInWithPassword({
    email,
    password,
    options: captchaToken ? { captchaToken } : undefined,
  })
}

export async function signOut() {
  return supabase.auth.signOut()
}

export async function getSession() {
  return supabase.auth.getSession()
}

// ─── Clients ─────────────────────────────────────────────────────────────────

export async function getClients(bookkeeperId) {
  return supabase
    .from('clients')
    .select('*')
    .eq('bookkeeper_id', bookkeeperId)
    .order('name')
}

export async function addClient(bookkeeperId, name, email, opts = {}) {
  // opts.remindersEnabled    : boolean, defaults to column default (true) when omitted
  // opts.internalReference   : string, optional free-text reference
  // opts.contactName         : string, optional person name (vs entity name)
  // opts.notes               : string, optional practice-visible notes
  const row = {
    bookkeeper_id: bookkeeperId,
    name,
    email,
  }
  if (typeof opts.remindersEnabled === 'boolean') {
    row.reminders_enabled = opts.remindersEnabled
  }
  if (opts.internalReference !== undefined) row.internal_reference = opts.internalReference || null
  if (opts.contactName       !== undefined) row.contact_name       = opts.contactName       || null
  if (opts.notes             !== undefined) row.notes              = opts.notes             || null

  return supabase
    .from('clients')
    .insert(row)
    .select()
    .single()
}

// Update one or more of the client's editable fields. Pass any subset of:
//   name, email, internalReference, contactName, notes
// Fields not present in the patch are left unchanged. Empty strings are
// converted to NULL so we don't store '' for "no value" (keeps the DB
// queries clean when checking "if (client.internal_reference)" etc.).
export async function updateClientDetails(clientId, patch) {
  const row = {}
  if (patch.name              !== undefined) row.name               = patch.name.trim()
  if (patch.email             !== undefined) row.email              = patch.email.trim()
  if (patch.internalReference !== undefined) row.internal_reference = patch.internalReference.trim() || null
  if (patch.contactName       !== undefined) row.contact_name       = patch.contactName.trim()       || null
  if (patch.notes             !== undefined) row.notes              = patch.notes // notes preserves whitespace; only NULL if explicitly empty string after trim
  // Coerce blank notes to NULL the same way as the others
  if (typeof row.notes === 'string' && row.notes.trim() === '') row.notes = null

  if (Object.keys(row).length === 0) return { data: null }

  return supabase
    .from('clients')
    .update(row)
    .eq('id', clientId)
    .select()
    .single()
}

// Toggle the per-client reminder opt-out. Pass true to receive reminders,
// false to skip all auto-reminders for this client's requests.
export async function updateClientReminders(clientId, enabled) {
  return supabase
    .from('clients')
    .update({ reminders_enabled: !!enabled })
    .eq('id', clientId)
}

export async function deleteClient(clientId) {
  return supabase.from('clients').delete().eq('id', clientId)
}

// Count the client's open requests: active (not cancelled), at least one
// pending transaction, and not yet complete. Used by the Dashboard archive
// flow to decide whether to show the cascade-cancel prompt.
//
// Returns { data: number, error }. Counts as a single-shot SQL query
// using a head: true Postgres count so we don't pay for the row data.
export async function countClientOpenRequests(clientId) {
  // We need requests for this client where:
  //   - cancelled_at is null
  //   - deletion_scheduled_at is null (not in retention pipeline)
  //   - deleted_at is null (not hard-deleted)
  //   - at least one transaction is still 'pending'
  // The transaction join + "EXISTS pending" is awkward via the JS client
  // without an RPC, so we do a two-step: pull request IDs, then count
  // those with at least one pending transaction. Volume is tiny so the
  // overhead is negligible.
  const { data: rows, error } = await supabase
    .from('requests')
    .select('id, transactions!inner(status)')
    .eq('client_id', clientId)
    .is('cancelled_at', null)
    .is('deletion_scheduled_at', null)
    .is('deleted_at', null)
    .eq('transactions.status', 'pending')
  if (error) return { data: 0, error }
  // De-duplicate request IDs (the join can return one row per pending
  // transaction). A Set on id gives the count of distinct requests.
  const ids = new Set((rows || []).map(r => r.id))
  return { data: ids.size }
}

export async function archiveClient(clientId, opts = {}) {
  // opts.cancelOpenRequests : if true, mark all of this client's active
  //                           non-cancelled requests as cancelled in the
  //                           same operation. Reuses the same cancellation
  //                           field used by the per-request Cancel button.
  if (opts.cancelOpenRequests) {
    const now = new Date().toISOString()
    const { error: cancelError } = await supabase
      .from('requests')
      .update({ cancelled_at: now })
      .eq('client_id', clientId)
      .is('cancelled_at', null)
    if (cancelError) return { error: cancelError }
  }
  return supabase
    .from('clients')
    .update({ archived: true })
    .eq('id', clientId)
}

export async function unarchiveClient(clientId) {
  return supabase
    .from('clients')
    .update({ archived: false })
    .eq('id', clientId)
}

// Returns outstanding (pending) transaction counts per client for the dashboard
export async function getClientOutstanding(bookkeeperId) {
  return supabase
    .from('client_outstanding')
    .select('*')
    .eq('bookkeeper_id', bookkeeperId)
}

// ─── Requests ────────────────────────────────────────────────────────────────

export async function getRequests(clientId) {
  // Pull transactions so we can count pending/uploaded/lost per request.
  // Filter out requests in any retention state (scheduled or deleted)
  // so the bookkeeper UI doesn't show requests that have crossed the
  // 7-year boundary. Transactions and metadata remain in the database
  // but they're treated as gone from the bookkeeper's perspective.
  return supabase
    .from('requests')
    .select('*, transactions(id, status)')
    .eq('client_id', clientId)
    .is('deletion_scheduled_at', null)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
}

export async function createRequest(clientId, bookkeeperId, title, bookkeeperNotes, transactions, opts = {}) {
  // opts.reminderEnabled        — boolean, per-request override; omit to use default (true)
  // opts.reminderDaysAfterSend  — integer, custom schedule for this request; omit to use practice default
  const token = crypto.randomUUID().replace(/-/g, '') + crypto.randomUUID().replace(/-/g, '')
  const row = {
    client_id: clientId,
    bookkeeper_id: bookkeeperId,
    title,
    token,
    bookkeeper_notes: bookkeeperNotes || null,
  }
  if (typeof opts.reminderEnabled === 'boolean') {
    row.reminder_enabled = opts.reminderEnabled
  }
  if (typeof opts.reminderDaysAfterSend === 'number' && !Number.isNaN(opts.reminderDaysAfterSend)) {
    row.reminder_days_after_send = opts.reminderDaysAfterSend
  }

  const { data: request, error } = await supabase
    .from('requests')
    .insert(row)
    .select()
    .single()

  if (error) return { error }

  const txRows = transactions.map((t, i) => ({
    request_id: request.id,
    date: t.date || null,
    description: t.description || '',
    amount: parseFloat(t.amount) || 0,
    category: t.category || '',
    payee: t.payee || null,
    sort_order: i,
  }))

  const { error: txError } = await supabase.from('transactions').insert(txRows)
  if (txError) return { error: txError }

  return { data: request }
}

// Update the per-request reminder controls. Pass either or both fields;
// pass null for daysAfterSend to fall back to the practice default.
export async function updateRequestReminderSettings(requestId, { enabled, daysAfterSend } = {}) {
  const patch = {}
  if (typeof enabled === 'boolean') patch.reminder_enabled = enabled
  if (daysAfterSend === null || typeof daysAfterSend === 'number') {
    patch.reminder_days_after_send = daysAfterSend
  }
  if (Object.keys(patch).length === 0) return { data: null }
  return supabase
    .from('requests')
    .update(patch)
    .eq('id', requestId)
    .select()
    .single()
}

export async function deleteRequest(requestId) {
  return supabase.from('requests').delete().eq('id', requestId)
}

// Mark a request as cancelled. Sets cancelled_at to now. Existing
// transactions and uploaded files are untouched; the client just can't
// upload anything new, and the auto-reminder scheduler will skip the
// request from this point on.
export async function cancelRequest(requestId) {
  return supabase
    .from('requests')
    .update({ cancelled_at: new Date().toISOString() })
    .eq('id', requestId)
    .select()
    .single()
}

// Reverse a cancellation. Clears cancelled_at so the request is treated as
// active again. Existing reminder_sent_at is preserved (the reminder
// already fired or didn't, depending on timing), so reopening doesn't
// suddenly cause a deluge of catch-up reminders.
export async function reopenRequest(requestId) {
  return supabase
    .from('requests')
    .update({ cancelled_at: null })
    .eq('id', requestId)
    .select()
    .single()
}

export async function updateRequestNotes(requestId, bookkeeperNotes) {
  return supabase
    .from('requests')
    .update({ bookkeeper_notes: bookkeeperNotes })
    .eq('id', requestId)
}

export async function getRequestByToken(token) {
  // Used by the unauthenticated client portal. Goes through portal_get_request
  // (SECURITY DEFINER) so we never expose the requests / clients / transactions
  // tables directly to anon. The RPC validates the token server-side and
  // returns NULL for any bad input, so a malformed or unknown token just
  // gives back "request not found".
  //
  // The RPC returns the same shape the rest of the app expects:
  //   { ...request fields, clients: {...}, transactions: [...],
  //     bookkeeper_email, bookkeeper_display_name, bookkeeper_practice_name,
  //     bookkeeper_notify }
  // So callers (ClientPortal.js) don't need to change.
  const { data, error } = await supabase.rpc('portal_get_request', { p_token: token })
  if (error)  return { data: null, error }
  if (!data)  return { data: null, error: { message: 'Request not found' } }
  return { data, error: null }
}

export async function getRequestById(id) {
  return supabase
    .from('requests')
    .select('*, clients(name, email, reminders_enabled, archived), transactions(*)')
    .eq('id', id)
    .single()
}

export async function updateRequestStatus(requestId, status) {
  return supabase.from('requests').update({ status }).eq('id', requestId)
}

// ─── Transactions ─────────────────────────────────────────────────────────────

export async function deleteTransaction(transactionId) {
  return supabase.from('transactions').delete().eq('id', transactionId)
}

// Append new transactions to an existing request. New rows get sort_order
// values starting from MAX(existing sort_order) + 1, so they show up at the
// end when sorted by sort_order but mix in naturally when sorted by date.
export async function addTransactionsToRequest(requestId, transactions) {
  if (!transactions || transactions.length === 0) return { data: [] }

  // Find the current max sort_order so new rows don't collide with existing
  // ones. If the table is empty (somehow) we just start at 0.
  const { data: existing, error: maxErr } = await supabase
    .from('transactions')
    .select('sort_order')
    .eq('request_id', requestId)
    .order('sort_order', { ascending: false })
    .limit(1)

  if (maxErr) return { error: maxErr }

  const startOrder = existing?.[0]?.sort_order != null
    ? existing[0].sort_order + 1
    : 0

  const rows = transactions.map((t, i) => ({
    request_id:  requestId,
    date:        t.date || null,
    description: t.description || '',
    amount:      parseFloat(t.amount) || 0,
    category:    t.category || '',
    payee:       t.payee || null,
    sort_order:  startOrder + i,
    // status defaults to 'pending' at the DB level
  }))

  return supabase.from('transactions').insert(rows).select()
}

// Update descriptions on multiple transactions in one round trip. Takes an
// object keyed by transaction id, value is the new description string.
// Empty strings are allowed (clears the description). Rows missing from
// the patch object are left untouched.
export async function updateTransactionDescriptions(updates) {
  const entries = Object.entries(updates)
  if (entries.length === 0) return { data: [] }

  // Postgres doesn't have a built-in bulk-update for different values per
  // row, so we issue one update per row in parallel. Volumes here are tiny
  // (a handful of rows selected for edit), so this is fine.
  const results = await Promise.all(
    entries.map(([id, description]) =>
      supabase.from('transactions').update({ description }).eq('id', id)
    )
  )
  const firstError = results.find(r => r.error)
  if (firstError) return { error: firstError.error }
  return { data: entries.map(([id]) => id) }
}


// Client marks a transaction as lost — stores explanation and business flag
// Mark a transaction as lost — used by both:
//   - Client portal (unauthenticated, passes `token` to go via portal RPC)
//   - Bookkeeper (authenticated; falls back to direct update under RLS)
export async function markTransactionLost(transactionId, explanation, whollyBusiness, token) {
  if (token) {
    const { error } = await supabase.rpc('portal_mark_lost', {
      p_token:           token,
      p_transaction_id:  transactionId,
      p_explanation:     explanation,
      p_wholly_business: whollyBusiness,
    })
    return { error }
  }
  return supabase
    .from('transactions')
    .update({
      status: 'lost',
      lost_explanation: explanation,
      lost_wholly_business: whollyBusiness,
      client_note: null, // clear any previous note
    })
    .eq('id', transactionId)
}

// Client (or bookkeeper) un-marks a lost transaction (back to pending)
export async function unmarkTransactionLost(transactionId, token) {
  if (token) {
    const { error } = await supabase.rpc('portal_unmark_lost', {
      p_token:          token,
      p_transaction_id: transactionId,
    })
    return { error }
  }
  return supabase
    .from('transactions')
    .update({
      status: 'pending',
      lost_explanation: null,
      lost_wholly_business: null,
    })
    .eq('id', transactionId)
}

// Save a client's note on a transaction
export async function saveClientNote(transactionId, note, token) {
  if (token) {
    const { error } = await supabase.rpc('portal_save_client_note', {
      p_token:          token,
      p_transaction_id: transactionId,
      p_note:           note,
    })
    return { error }
  }
  return supabase
    .from('transactions')
    .update({ client_note: note })
    .eq('id', transactionId)
}

// Save a bookkeeper's internal note on a transaction. Visible to all members
// of the practice. NOT shown to the client.
export async function saveBookkeeperNote(transactionId, note) {
  return supabase
    .from('transactions')
    .update({ bookkeeper_note: note })
    .eq('id', transactionId)
}

// ─── File uploads ─────────────────────────────────────────────────────────────

export async function uploadReceipt(transactionId, requestToken, file) {
  // Two-stage upload:
  //   1. Upload the file bytes to storage (still public bucket — locked down
  //      in Step 2 with signed URLs).
  //   2. Insert the attachments row via portal_insert_attachment, which:
  //        - validates the token
  //        - verifies the transaction belongs to that token's request
  //        - rejects uploads when the request is cancelled / in retention
  //        - rejects uploads when the client is archived
  //        - validates the file_path starts with the token
  //        - updates the transaction status to 'uploaded'
  //
  // If the RPC fails, we delete the just-uploaded storage object so we
  // don't leave orphaned bytes behind that no row in the DB points to.

  const ext = file.name.split('.').pop()
  const path = `${requestToken}/${transactionId}/${Date.now()}.${ext}`

  const { error: uploadError } = await supabase.storage
    .from('receipts')
    .upload(path, file)

  if (uploadError) return { error: uploadError }

  const { data: urlData } = supabase.storage.from('receipts').getPublicUrl(path)

  const { data, error } = await supabase.rpc('portal_insert_attachment', {
    p_token:          requestToken,
    p_transaction_id: transactionId,
    p_file_name:      file.name,
    p_file_path:      path,
    p_file_url:       urlData.publicUrl,
    p_file_size:      file.size,
    p_mime_type:      file.type,
  })

  if (error) {
    // Roll back the storage write so we don't leave an orphan
    await supabase.storage.from('receipts').remove([path]).catch(() => {})

    // Map the server-side validation messages to the same client-facing
    // strings the old code used, so the UI stays consistent
    const msg = error.message || ''
    if (msg.includes('cancelled'))    return { error: { message: 'This request has been cancelled. Please contact your bookkeeper.' } }
    if (msg.includes('expired'))      return { error: { message: 'This request is no longer active. Please contact your bookkeeper.' } }
    if (msg.includes('archived'))     return { error: { message: 'This client account is archived. Please contact your bookkeeper.' } }
    return { error }
  }

  return { data }
}

// Get attachments for a transaction.
//   - Client portal (unauthenticated, passes `token`): goes via the
//     portal_get_attachments RPC, which validates the transaction belongs
//     to the request matching that token.
//   - Bookkeeper (authenticated): falls back to direct table read under RLS.
export async function getAttachments(transactionId, token) {
  if (token) {
    return supabase.rpc('portal_get_attachments', {
      p_token:          token,
      p_transaction_id: transactionId,
    })
  }
  return supabase
    .from('attachments')
    .select('*')
    .eq('transaction_id', transactionId)
    .order('created_at')
}

export async function deleteAttachment(attachmentId, filePath, transactionId) {
  await supabase.storage.from('receipts').remove([filePath])
  const result = await supabase.from('attachments').delete().eq('id', attachmentId)

  // After deleting an attachment, check if any remain — if not, revert status to pending
  if (!result.error && transactionId) {
    const { data: remaining } = await supabase
      .from('attachments')
      .select('id')
      .eq('transaction_id', transactionId)
    if (!remaining || remaining.length === 0) {
      await supabase
        .from('transactions')
        .update({ status: 'pending' })
        .eq('id', transactionId)
    }
  }

  return result
}

// Deletes an attachment and reverts transaction to pending if no files remain.
//   - Client portal (unauthenticated, passes `token`): goes via
//     portal_delete_attachment which validates the attachment belongs to
//     the request matching that token, deletes the DB row, and reverts
//     status. Storage file is removed client-side after the RPC succeeds.
//   - Bookkeeper (authenticated): falls back to the original direct path
//     under RLS.
export async function deleteAttachmentAndRevert(attachmentId, filePath, transactionId, token) {
  if (token) {
    // RPC validates ownership, deletes the row, and handles status revert.
    const { error: rpcError } = await supabase.rpc('portal_delete_attachment', {
      p_token:         token,
      p_attachment_id: attachmentId,
    })
    if (rpcError) return { error: rpcError }

    // Storage object is still public-readable until Step 2 (signed URLs).
    // Remove it now that the DB row is gone so the file isn't orphaned.
    // Log on failure but don't surface — the DB delete already succeeded
    // and that's what the UI cares about.
    const { error: storageError } = await supabase.storage.from('receipts').remove([filePath])
    if (storageError) console.error('Storage delete failed:', storageError.message)

    return { error: null }
  }

  const { error: storageError } = await supabase.storage.from('receipts').remove([filePath])
  if (storageError) console.error('Storage delete failed:', storageError.message)

  const { error: dbError } = await supabase.from('attachments').delete().eq('id', attachmentId)
  if (dbError) return { error: dbError }

  const { data: remaining } = await supabase
    .from('attachments')
    .select('id')
    .eq('transaction_id', transactionId)

  if (!remaining || remaining.length === 0) {
    await supabase
      .from('transactions')
      .update({ status: 'pending' })
      .eq('id', transactionId)
  }

  return { error: null }
}

// Generate a 1-hour signed URL for viewing or downloading a receipt file.
//
// Two paths:
//   - Bookkeeper (no token): calls Supabase Storage directly. The bookkeeper
//     has a real auth session and the storage SELECT policy on the receipts
//     bucket lets them read their own files.
//   - Client portal (token provided): calls the portal-signed-url Edge
//     Function, which validates the token + path on the server using the
//     service-role key. The portal can't call createSignedUrl directly
//     because, after Commit B, the storage policy won't grant the anon
//     role read access.
//
// Returns { signedUrl, error }. The caller should treat signedUrl as
// short-lived (1 hour) — regenerate on every view, don't cache.
export async function getReceiptSignedUrl(filePath, token) {
  if (token) {
    const { data, error } = await supabase.functions.invoke('portal-signed-url', {
      body: { token, file_path: filePath },
    })
    if (error)        return { signedUrl: null, error }
    if (data?.error)  return { signedUrl: null, error: { message: data.error } }
    return { signedUrl: data?.signed_url || null, error: null }
  }

  const { data, error } = await supabase
    .storage
    .from('receipts')
    .createSignedUrl(filePath, 3600)
  if (error) return { signedUrl: null, error }
  return { signedUrl: data?.signedUrl || null, error: null }
}

// ─── File retention / cleanup ─────────────────────────────────────────────────

// Removes all stored files for a completed request once the bookkeeper has
// confirmed they've saved them elsewhere. Deletes files from storage and
// clears attachment records, but keeps transaction rows and metadata intact
// as a permanent record. Part of the 7-year retention policy.
export async function cleanupRequestFiles(requestId) {
  // Get all transaction IDs for this request
  const { data: txs, error: txError } = await supabase
    .from('transactions')
    .select('id')
    .eq('request_id', requestId)

  if (txError) return { error: txError }

  const txIds = (txs || []).map(t => t.id)
  if (txIds.length === 0) return { data: { removed: 0 } }

  // Get all attachments for those transactions
  const { data: attachments, error: attError } = await supabase
    .from('attachments')
    .select('id, file_path')
    .in('transaction_id', txIds)

  if (attError) return { error: attError }
  if (!attachments || attachments.length === 0) return { data: { removed: 0 } }

  // Delete all files from storage in one call
  const filePaths = attachments.map(a => a.file_path)
  await supabase.storage.from('receipts').remove(filePaths)

  // Delete all attachment records
  const { error: delError } = await supabase
    .from('attachments')
    .delete()
    .in('id', attachments.map(a => a.id))

  if (delError) return { error: delError }

  return { data: { removed: attachments.length } }
}

// ─── Bookkeeper settings ──────────────────────────────────────────────────────

export async function getSettings(bookkeeperId) {
  const { data, error } = await supabase
    .from('bookkeeper_settings')
    .select('*')
    .eq('bookkeeper_id', bookkeeperId)
    .maybeSingle()
  return { data, error }
}

export async function saveSettings(bookkeeperId, settings) {
  return supabase
    .from('bookkeeper_settings')
    .upsert({ bookkeeper_id: bookkeeperId, ...settings }, { onConflict: 'bookkeeper_id' })
    .select()
    .single()
}

// Change the logged-in user's email address via Supabase Auth
export async function updateEmail(newEmail) {
  return supabase.auth.updateUser({ email: newEmail })
}

// Change the logged-in user's password via Supabase Auth
export async function updatePassword(newPassword) {
  return supabase.auth.updateUser({ password: newPassword })
}

// ─── Email sending (via Edge Function) ────────────────────────────────────────

// Calls the send-email Edge Function for both client request emails
// and bookkeeper completion notifications.
async function callSendEmail(payload) {
  const { data, error } = await supabase.functions.invoke('send-email', {
    body: payload,
  })
  if (error) return { error }
  return { data }
}

export async function sendClientRequestEmail({
  clientEmail,
  clientName,
  bookkeeperName,
  requestTitle,
  requestLink,
  subjectTemplate,
  bodyTemplate,
}) {
  return callSendEmail({
    type:               'client_request',
    client_email:       clientEmail,
    client_name:        clientName,
    bookkeeper_name:    bookkeeperName,
    request_title:      requestTitle,
    request_link:       requestLink,
    subject_template:   subjectTemplate || null,
    body_template:      bodyTemplate || null,
  })
}

export async function sendCompletionNotification({
  bookkeeperEmail,
  bookkeeperName,
  clientName,
  requestTitle,
  requestLink,
  uploadedCount,
  lostCount,
}) {
  return callSendEmail({
    type:               'bookkeeper_notify',
    bookkeeper_email:   bookkeeperEmail,
    bookkeeper_name:    bookkeeperName,
    client_name:        clientName,
    request_title:      requestTitle,
    request_link:       requestLink,
    uploaded_count:     uploadedCount,
    lost_count:         lostCount,
  })
}

// ─── Subscription ─────────────────────────────────────────────────────────────

// Look up the subscription state that gates access for this user.
//
// A practice has exactly one subscription -- the owner's. Team members
// (Admin / Assistant) inherit access from their practice owner's
// subscription. So when a team member calls getSubscription, we need
// to resolve their owner's bookkeeper_settings row, not their own.
//
// Resolution order:
//   1. If the user is in practice_members with role != 'owner', return
//      the OWNER's settings (looked up via practice_members.practice_id,
//      which equals the owner's user id).
//   2. Otherwise (owner, or not in practice_members at all), return
//      their own settings row.
//
// Maintains the same { data, error } return shape as before, so
// callers don't need to know about resolution. data is still a single
// subscription object (or null) with the same columns.
export async function getSubscription(bookkeeperId) {
  // First, check if this user is a non-owner member of a practice
  const { data: membership } = await supabase
    .from('practice_members')
    .select('practice_id, role')
    .eq('user_id', bookkeeperId)
    .maybeSingle()

  // If they're a team member (admin / assistant), redirect to the owner.
  // If they're listed as the owner themselves, fall through to their
  // own settings row -- which IS the practice's subscription.
  const targetId = (membership && membership.role !== 'owner')
    ? membership.practice_id
    : bookkeeperId

  return supabase
    .from('bookkeeper_settings')
    .select('subscription_status, subscription_tier, trial_ends_at, current_period_ends_at, stripe_customer_id, cancel_at_period_end, client_count')
    .eq('bookkeeper_id', targetId)
    .maybeSingle()
}

// Returns true if the bookkeeper has access (trialing or active/cancelling
// subscription, within their paid period).
//
// IMPORTANT: for 'active' subscriptions, we ALSO check current_period_ends_at.
// Stripe webhook delivery is at-least-once but not guaranteed-once: if a
// customer.subscription.deleted event is missed, the row stays at 'active'
// with a stale period_end. Without this check, that user would have
// indefinite access. With this check, they lose access at period_end + 1
// second even if the webhook never arrives.
//
// Stripe sends a renewal webhook immediately on renewal, so the period_end
// is normally updated to the new date within seconds. If we're more than
// a few seconds past period_end on an 'active' row with no webhook
// update, the webhook isn't coming -- treat as expired.
//
// MUST stay in sync with the SQL function bookkeeper_has_access() defined
// in 20260518_ninety_day_closure_retention.sql, which the SQL workers
// (closure cron, welcome email cron) use to filter candidates.
export function hasAccess(sub) {
  if (!sub) return false
  // Active subscription, but only if the paid period hasn't expired.
  if (sub.subscription_status === 'active') {
    if (!sub.current_period_ends_at) return true  // No period date stored yet? Trust the status.
    return new Date(sub.current_period_ends_at) > new Date()
  }
  // Canceled but still within the paid billing period, keep access until it expires
  if (sub.subscription_status === 'canceled' && sub.current_period_ends_at) {
    return new Date(sub.current_period_ends_at) > new Date()
  }
  // Trial still running
  if (sub.subscription_status === 'trialing') {
    const trialEnd = new Date(sub.trial_ends_at)
    return trialEnd > new Date()
  }
  return false
}

// Returns days remaining in trial (0 if expired or not trialing)
export function trialDaysRemaining(sub) {
  if (!sub || sub.subscription_status !== 'trialing') return 0
  const diff = new Date(sub.trial_ends_at) - new Date()
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

// Create a Stripe Checkout session and redirect to it
export async function createCheckoutSession(tier) {
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.access_token) {
    return { error: { message: 'You need to be signed in to subscribe. Please sign in and try again.' } }
  }

  const { data, error } = await supabase.functions.invoke('create-checkout', {
    body: { tier },
    headers: { Authorization: `Bearer ${session.access_token}` },
  })
  if (error) return { error }
  if (data?.error) return { error: { message: data.error } }
  if (data?.url) window.location.href = data.url
  return { data }
}

// Open Stripe Billing Portal — cancel, invoices, payment method
export async function createBillingPortalSession() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) {
    return { error: { message: 'You need to be signed in to manage billing.' } }
  }
  const { data, error } = await supabase.functions.invoke('create-billing-portal', {
    body: {},
    headers: { Authorization: `Bearer ${session.access_token}` },
  })
  if (error) return { error }
  if (data?.error) return { error: { message: data.error } }
  if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer')
  return { data }
}

// Switch between per-client and pro plans directly via the API
// (bypasses Stripe portal to avoid quantity carrying over)
export async function switchPlan(tier) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) {
    return { error: { message: 'You need to be signed in to change plans.' } }
  }
  const { data, error } = await supabase.functions.invoke('switch-plan', {
    body: { tier },
    headers: { Authorization: `Bearer ${session.access_token}` },
  })
  if (error) return { error }
  if (data?.error) return { error: { message: data.error } }
  return { data }
}

// ─── Practice / team ──────────────────────────────────────────────────────────

// Get the current user's role and practice_id
export async function getMyMembership() {
  const { data: { user } } = await supabase.auth.getUser()
  const { data, error } = await supabase
    .from('practice_members')
    .select('role, practice_id')
    .eq('user_id', user?.id)
    .limit(1)
  if (error) return { data: null, error }
  return { data: data?.[0] ?? null, error: null }
}

// Get all members of the current user's practice
export async function getPracticeMembers(practiceId) {
  const { data, error } = await supabase
    .from('practice_members')
    .select('id, user_id, role, invited_at')
    .eq('practice_id', practiceId)
  if (error) return { data, error }
  // Fetch emails for each member via a separate RPC
  const { data: emails } = await supabase.rpc('get_member_emails', { p_practice_id: practiceId })
  const emailMap = {}
  for (const row of (emails || [])) emailMap[row.user_id] = row.email
  return {
    data: (data || []).map(m => ({ ...m, email: emailMap[m.user_id] || '' })),
    error: null,
  }
}

// Get pending invitations for the practice
export async function getPracticeInvitations(practiceId) {
  return supabase
    .from('practice_invitations')
    .select('id, invitee_email, role, expires_at, accepted, created_at')
    .eq('practice_id', practiceId)
    .eq('accepted', false)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
}

// Invite a new team member
export async function inviteMember(inviteeEmail, role, practiceName) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) return { error: { message: 'Not authenticated' } }
  const { data, error } = await supabase.functions.invoke('invite-member', {
    body: { invitee_email: inviteeEmail, role, practice_name: practiceName },
    headers: { Authorization: `Bearer ${session.access_token}` },
  })
  if (error) return { error }
  if (data?.error) return { error: { message: data.error } }
  return { data }
}

// Remove a team member (owner only)
//
// Two steps, in order:
//   1. Fetch the user_id from the practice_members row before deleting it.
//   2. Delete the practice_members row.
//   3. Set subscription_status = 'removed' on the user's bookkeeper_settings
//      row so they're locked out of the app. Their auth.users record and
//      personal settings (name, phone, etc) stay intact -- they can be
//      re-invited cleanly, and we don't lose their human identity.
//
// If step 1 fails, nothing changes. If step 2 fails, nothing changes.
// If step 3 fails after step 2 succeeded, we end up with a removed
// user who can still sign in (subscription_status whatever it was
// before, probably 'team_member'). hasAccess returns false for
// 'team_member' so they STILL get locked out -- the lockout in step 3
// is belt-and-braces in case the resolution logic is ever bypassed.
export async function removeMember(memberId) {
  // Step 1: look up the user_id for this membership row
  const { data: row, error: fetchErr } = await supabase
    .from('practice_members')
    .select('user_id')
    .eq('id', memberId)
    .maybeSingle()
  if (fetchErr) return { error: fetchErr }
  if (!row)     return { error: { message: 'Membership not found' } }

  // Step 2: delete the practice_members row
  const { error: delErr } = await supabase
    .from('practice_members')
    .delete()
    .eq('id', memberId)
  if (delErr) return { error: delErr }

  // Step 3: lock the user out via the sentinel status
  const { error: lockErr } = await supabase
    .from('bookkeeper_settings')
    .update({ subscription_status: 'removed' })
    .eq('bookkeeper_id', row.user_id)
  if (lockErr) {
    // Soft-fail: the practice_members row is gone, the user's resolution
    // logic will find no owner and fall through to their own row, where
    // status is still 'team_member' and hasAccess returns false anyway.
    // Log it but don't surface to the caller.
    console.error('removeMember: could not set removed sentinel:', lockErr.message)
  }

  return { data: { removed: true } }
}

// Update a member's role
export async function updateMemberRole(memberId, role) {
  return supabase.from('practice_members').update({ role }).eq('id', memberId)
}

// Cancel a pending invitation
export async function cancelInvitation(invitationId) {
  return supabase.from('practice_invitations').delete().eq('id', invitationId)
}

// Accept an invitation (called from the join page)
export async function acceptInvitation(token) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) return { error: { message: 'Not authenticated' } }
  const { data, error } = await supabase.functions.invoke('accept-invitation', {
    body: { token },
    headers: { Authorization: `Bearer ${session.access_token}` },
  })
  if (error) return { error }
  if (data?.error) return { error: { message: data.error } }
  return { data }
}

// Look up an invitation by token (to show details on the join page).
// Goes through portal_get_invitation (SECURITY DEFINER) so we never expose
// the practice_invitations table directly to anon. The RPC validates the
// token shape and returns NULL for any bad input. The response also includes
// a `practice_name` field so the join page can show what the user is joining.
export async function getInvitationByToken(token) {
  const { data, error } = await supabase.rpc('portal_get_invitation', { p_token: token })
  if (error) return { data: null, error }
  if (!data) return { data: null, error: null }  // not found: matches old maybeSingle() contract
  return { data, error: null }
}
